// Components/HorizontalScrollbar/HorizontalScrollbar.js
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './HorizontalScrollbar.css';

const HorizontalScrollbar = ({ categories = [] }) => {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');

  // Default categories for backward compatibility
  const defaultCategories = [
    { _id: 'all', name: 'All', slug: 'all', path: '/' },
    { _id: 'mens', name: 'Mens', slug: 'mens', path: '/mens' },
    { _id: 'womens', name: 'Womens', slug: 'womens', path: '/womens' },
    { _id: 'kids', name: 'Kids', slug: 'kids', path: '/kids' },
  ];

  // Combine default categories with dynamic ones from admin
  const allCategories = [
    ...defaultCategories,
    ...categories
      .filter(cat => cat.status === 'active')
      .map(cat => ({
        ...cat,
        path: `/${cat.slug}`
      }))
  ];

  // Remove duplicates in case admin added categories with same slugs as defaults
  const uniqueCategories = allCategories.filter((cat, index, self) =>
    index === self.findIndex((c) => c.slug === cat.slug)
  );

  // Set active category based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    
    if (currentPath === '/') {
      setActiveCategory('all');
    } else {
      const pathSegments = currentPath.split('/');
      const currentSlug = pathSegments[1]; // Get the first segment after /
      
      const matchedCategory = uniqueCategories.find(cat => 
        cat.slug === currentSlug || 
        (currentSlug === 'product' && cat.slug === 'all')
      );
      
      if (matchedCategory) {
        setActiveCategory(matchedCategory.slug);
      } else {
        setActiveCategory('all');
      }
    }
  }, [location.pathname, uniqueCategories]);

  // Drag to scroll functionality
  const handleMouseDown = (e) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      if (scrollRef.current) {
        scrollRef.current.style.cursor = 'grab';
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.removeProperty('user-select');
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  // Handle category click - navigate to the route
  const handleCategoryClick = (category) => {
    setActiveCategory(category.slug);
    navigate(category.path);
  };

  // Handle touch events for mobile
  const handleTouchStart = (e) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    const touch = e.touches[0];
    setStartX(touch.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const x = touch.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (uniqueCategories.length <= 1) {
    return null; // Don't show scrollbar if only "All" category exists
  }

  return (
    <div className="horizontal-scrollbar-container">
      {/* Scrollable Content - No arrows */}
      <div
        ref={scrollRef}
        className="horizontal-scrollbar"
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="scroll-content">
          {uniqueCategories.map((category) => (
            <button
              key={category._id}
              className={`category-item ${
                activeCategory === category.slug ? 'active' : ''
              }`}
              onClick={() => handleCategoryClick(category)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HorizontalScrollbar;
